// core/action/expenseCategoryActions.js
import { expenseCategoryAPI } from './api';
import { toast } from 'react-toastify';

export const expenseCategoryActions = {
    // Get categories list with pagination
    getCategories: async (params = {}) => {
        try {
            const response = await expenseCategoryAPI.list(params);
            
            console.log('Raw Categories API Response:', response);
            
            if (response && response.data) {
                // Your API returns { success: true, data: {...} }
                if (response.success === true && response.data) {
                    const apiData = response.data;
                    return {
                        success: true,
                        data: apiData.categories || [],
                        total: apiData.total || 0,
                        page: apiData.page || 1,
                        limit: apiData.limit || 10,
                        totalPages: apiData.total_pages || 1
                    };
                }
            }
            
            return {
                success: false,
                message: 'Invalid API response format',
                data: [],
                total: 0,
                page: 1,
                limit: 10,
                totalPages: 1
            };
        } catch (error) {
            console.error('Error fetching categories:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch categories',
                data: [],
                total: 0,
                page: 1,
                limit: 10,
                totalPages: 1
            };
        }
    },

    // Get single category
    getCategory: async (id) => {
        try {
            const response = await expenseCategoryAPI.get(id);
            
            if (response && response.data) {
                if (response.success && response.data) {
                    return {
                        success: true,
                        data: response.data
                    };
                }
            }
            
            return {
                success: false,
                message: 'Invalid API response format',
                data: null
            };
        } catch (error) {
            console.error('Error fetching category:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch category',
                data: null
            };
        }
    },

    // Create category
    createCategory: async (categoryData) => {
        try {
            if (!categoryData.category_name) {
                return { success: false, message: 'Category name is required' };
            }
            
            const response = await expenseCategoryAPI.create(categoryData);
            
            if (response && response.data) {
                if (response.success) {
                    toast.success(response.message || 'Category created successfully');
                    return {
                        success: true,
                        data: response.data,
                        message: response.message || 'Category created successfully'
                    };
                } else {
                    toast.error(response.message || 'Failed to create category');
                    return {
                        success: false,
                        message: response.message || 'Failed to create category'
                    };
                }
            }
            
            toast.error('Failed to create category');
            return {
                success: false,
                message: 'Failed to create category'
            };
        } catch (error) {
            console.error('Error creating category:', error);
            toast.error(error.message || 'Failed to create category');
            return {
                success: false,
                message: error.message || 'Failed to create category'
            };
        }
    },

    // Update category
    updateCategory: async (id, categoryData) => {
        try {
            if (!id) {
                return { success: false, message: 'Category ID is required' };
            }
            
            if (!categoryData.category_name) {
                return { success: false, message: 'Category name is required' };
            }
            
            categoryData.id = id;
            
            const response = await expenseCategoryAPI.update(id, categoryData);
            
            if (response && response.data) {
                if (response.success) {
                    toast.success(response.message || 'Category updated successfully');
                    return {
                        success: true,
                        data: response.data,
                        message: response.message || 'Category updated successfully'
                    };
                } else {
                    toast.error(response.message || 'Failed to update category');
                    return {
                        success: false,
                        message: response.message || 'Failed to update category'
                    };
                }
            }
            
            toast.error('Failed to update category');
            return {
                success: false,
                message: 'Failed to update category'
            };
        } catch (error) {
            console.error('Error updating category:', error);
            toast.error(error.message || 'Failed to update category');
            return {
                success: false,
                message: error.message || 'Failed to update category'
            };
        }
    },

    // Delete category
    deleteCategory: async (id) => {
        try {
            if (!id) {
                return { success: false, message: 'Category ID is required' };
            }
            
            const response = await expenseCategoryAPI.delete({ id });
            
            if (response && response.data) {
                if (response.success) {
                    toast.success(response.message || 'Category deleted successfully');
                    return {
                        success: true,
                        message: response.message || 'Category deleted successfully'
                    };
                } else {
                    toast.error(response.message || 'Failed to delete category');
                    return {
                        success: false,
                        message: response.message || 'Failed to delete category'
                    };
                }
            }
            
            toast.error('Failed to delete category');
            return {
                success: false,
                message: 'Failed to delete category'
            };
        } catch (error) {
            console.error('Error deleting category:', error);
            toast.error(error.message || 'Failed to delete category');
            return {
                success: false,
                message: error.message || 'Failed to delete category'
            };
        }
    },

    // Format currency helper
    formatCurrency: (amount, currency = 'INR') => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    },

    // Format date helper
    formatDate: (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    },

    // Get status badge class
    getStatusBadge: (status) => {
        return status === 'active' ? 'bg-success' : 'bg-danger';
    },

    // Get icon options for dropdown
    getIconOptions: () => [
        { value: 'Folder', label: '📁 Folder' },
        { value: 'Briefcase', label: '💼 Briefcase' },
        { value: 'Home', label: '🏠 Home' },
        { value: 'Zap', label: '⚡ Zap' },
        { value: 'Users', label: '👥 Users' },
        { value: 'MapPin', label: '📍 Map Pin' },
        { value: 'TrendingUp', label: '📈 Trending Up' },
        { value: 'Tool', label: '🔧 Tool' },
        { value: 'DollarSign', label: '💰 Dollar' },
        { value: 'ShoppingCart', label: '🛒 Cart' },
        { value: 'Truck', label: '🚚 Truck' },
        { value: 'Package', label: '📦 Package' }
    ],

    // Get color options for dropdown
    getColorOptions: () => [
        { value: '#0dcaf0', label: 'Cyan', color: '#0dcaf0' },
        { value: '#fd7e14', label: 'Orange', color: '#fd7e14' },
        { value: '#198754', label: 'Green', color: '#198754' },
        { value: '#0d6efd', label: 'Blue', color: '#0d6efd' },
        { value: '#6f42c1', label: 'Purple', color: '#6f42c1' },
        { value: '#d63384', label: 'Pink', color: '#d63384' },
        { value: '#20c997', label: 'Teal', color: '#20c997' },
        { value: '#dc3545', label: 'Red', color: '#dc3545' },
        { value: '#ffc107', label: 'Yellow', color: '#ffc107' },
        { value: '#6c757d', label: 'Gray', color: '#6c757d' }
    ]
};

export default expenseCategoryActions;